export const ASSISTANT_CHAT_USER_ERROR = {
  generic: "Something went wrong. Please try again.",
  conversationSync:
    "This conversation got out of sync. Start a new conversation below, then describe the role you want to create.",
  providerUnavailable:
    "The assistant could not reach the AI service. Please try again in a moment.",
  reviewConnection:
    "The review connection closed. The steps above are what finished before it dropped.",
} as const;

function tryParseJsonObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

/** True when assistant text looks like a serialized provider/API failure, not normal prose. */
export function isTechnicalAssistantChatErrorText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (
    trimmed.includes("AI_APICallError") ||
    trimmed.includes("requestBodyValues") ||
    trimmed.includes('"stack":')
  ) {
    return true;
  }
  const parsed = tryParseJsonObject(trimmed);
  if (parsed) {
    if (typeof parsed.name === "string" && parsed.name.includes("APICallError")) return true;
    if (typeof parsed.message === "string" && /invalid_request_error|duplicate item/i.test(parsed.message)) {
      return true;
    }
    if (typeof parsed.error === "string") return true;
  }
  return false;
}

/** Map raw errors (useChat, streams, JSON blobs) to short user-facing copy. */
export function mapAssistantChatUserError(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return ASSISTANT_CHAT_USER_ERROR.generic;

  const parsed = tryParseJsonObject(trimmed);
  if (parsed) {
    if (typeof parsed.error === "string" && parsed.error.trim()) {
      return mapAssistantChatUserError(parsed.error);
    }
    if (typeof parsed.message === "string" && parsed.message.trim()) {
      return mapAssistantChatUserError(parsed.message);
    }
  }

  if (/duplicate item found/i.test(trimmed)) {
    return ASSISTANT_CHAT_USER_ERROR.conversationSync;
  }
  if (/model or endpoint not found|fallback_exhausted/i.test(trimmed)) {
    return ASSISTANT_CHAT_USER_ERROR.providerUnavailable;
  }
  if (/^network error$/i.test(trimmed) || /failed to fetch|networkerror|load failed/i.test(trimmed)) {
    return ASSISTANT_CHAT_USER_ERROR.reviewConnection;
  }
  if (/AI_APICallError|invalid_request_error/i.test(trimmed)) {
    return ASSISTANT_CHAT_USER_ERROR.generic;
  }
  if (trimmed.length > 240 || trimmed.includes("at <anonymous>") || trimmed.includes("processTicksAndRejections")) {
    return ASSISTANT_CHAT_USER_ERROR.generic;
  }

  return trimmed;
}

/** Use when rendering assistant markdown in write-assistant panels. */
export function sanitizeAssistantChatDisplayText(text: string): string {
  if (!isTechnicalAssistantChatErrorText(text)) return text;
  return mapAssistantChatUserError(text);
}

export function isConversationSyncAssistantError(raw: string): boolean {
  return mapAssistantChatUserError(raw) === ASSISTANT_CHAT_USER_ERROR.conversationSync;
}
